/**
 * Quantity stepper inside the header cart dropdown.
 *
 * Updates a line via /cart/change.js, then re-renders the header cart from
 * the section rendering API so prices, counts, and line items stay in sync.
 */
(function () {
  'use strict';

  var CHANGE_DELAY = 400;
  var routes = (window.theme && window.theme.routes) || {};
  var changeUrl = (routes.cart_change_url || '/cart/change') + '.js';
  var clearUrl = (routes.cart_clear_url || ((routes.cart_url || '/cart') + '/clear')) + '.js';
  var searchUrl = routes.search_url || '/search';

  var updating = false;
  var inputTimer = null;

  function headerCarts() {
    return document.querySelectorAll('.page-header .header-cart, .docked-navigation-container .header-cart');
  }

  function closestCart(el) {
    return el && el.closest ? el.closest('.header-cart') : null;
  }

  function setOpen(on) {
    var nodes = headerCarts();
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].classList.toggle('header-cart--open', on);
    }
  }

  function setUpdating(on) {
    updating = on;
    var summaries = document.querySelectorAll('.header-cart .cart-summary');
    for (var i = 0; i < summaries.length; i++) {
      summaries[i].classList.toggle('cart-summary--updating', on);
    }
  }

  function getHeaderSectionId() {
    var header = document.querySelector('[data-section-type="header"]');
    if (!header) return '';
    var wrapper = header.closest('[id^="shopify-section-"]');
    if (wrapper && wrapper.id) {
      return wrapper.id.replace('shopify-section-', '');
    }
    return header.getAttribute('data-section-id') || '';
  }

  function applyHeaderHtml(html, keepOpen) {
    var doc = document.createElement('div');
    doc.innerHTML = html;
    var selectors = ['.page-header .header-cart', '.docked-navigation-container .header-cart'];
    for (var i = 0; i < selectors.length; i++) {
      var curr = document.querySelector(selectors[i]);
      var next = doc.querySelector(selectors[i]);
      if (curr && next) {
        curr.innerHTML = next.innerHTML;
        if (keepOpen) curr.classList.add('header-cart--open');
      }
    }
  }

  function refreshHeaderCart() {
    var keepOpen = !!document.querySelector('.header-cart.header-cart--open');
    var sectionId = getHeaderSectionId();

    function fromSearchPage() {
      return fetch(searchUrl, { credentials: 'same-origin' })
        .then(function (res) { return res.text(); })
        .then(function (html) { applyHeaderHtml(html, keepOpen); });
    }

    if (!sectionId) return fromSearchPage();

    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return fetch(root + '?sections=' + encodeURIComponent(sectionId), {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('section fetch failed');
        return res.json();
      })
      .then(function (data) {
        var html = data[sectionId];
        if (!html) throw new Error('missing section html');
        applyHeaderHtml(html, keepOpen);
      })
      .catch(fromSearchPage);
  }

  function parseQty(value) {
    var qty = parseInt(value, 10);
    if (isNaN(qty) || qty < 0) return 0;
    return qty;
  }

  function clearCart(button) {
    if (!button || updating) return;

    var message = button.getAttribute('data-confirm');
    if (message && !window.confirm(message)) return;

    setOpen(true);
    setUpdating(true);

    fetch(clearUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart clear failed');
        return res.json();
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('pb:cart-changed'));
        if (document.body && document.body.classList.contains('template-cart')) {
          window.location.reload();
          return;
        }
        return refreshHeaderCart();
      })
      .catch(function () {
        return refreshHeaderCart();
      })
      .then(function () {
        setUpdating(false);
      });
  }

  function changeLine(stepper, quantity) {
    if (!stepper || updating) return;

    var key = stepper.getAttribute('data-key');
    var current = parseQty(stepper.getAttribute('data-qty'));
    var maxAttr = stepper.getAttribute('data-max');
    var max = maxAttr ? parseQty(maxAttr) : null;

    if (max !== null && quantity > max) quantity = max;
    if (quantity === current) return;

    var input = stepper.querySelector('.cart-summary__qty-input');
    if (input) input.value = quantity;
    stepper.setAttribute('data-qty', String(quantity));

    setOpen(true);
    setUpdating(true);

    fetch(changeUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: key,
        quantity: quantity
      })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) {
            var err = new Error((data && (data.description || data.message)) || 'Cart update failed');
            err.payload = data;
            throw err;
          }
          return data;
        });
      })
      .then(function () {
        return refreshHeaderCart();
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('pb:cart-changed'));
      })
      .catch(function () {
        return refreshHeaderCart();
      })
      .then(function () {
        setUpdating(false);
      });
  }

  document.addEventListener('click', function (evt) {
    var target = evt.target;
    if (!target || typeof target.closest !== 'function') return;

    var clearBtn = target.closest('[data-cart-clear]');
    if (clearBtn) {
      evt.preventDefault();
      evt.stopPropagation();
      clearCart(clearBtn);
      return;
    }

    var removeBtn = target.closest('[data-cart-qty-remove]');
    if (removeBtn) {
      var removeStepper = removeBtn.closest('[data-cart-qty]');
      if (removeStepper) {
        evt.preventDefault();
        evt.stopPropagation();
        changeLine(removeStepper, 0);
        return;
      }
    }

    var button = target.closest('[data-cart-qty-plus], [data-cart-qty-minus]');
    if (!button) return;

    var stepper = button.closest('[data-cart-qty]');
    if (!stepper) return;

    evt.preventDefault();
    evt.stopPropagation();

    if (button.classList.contains('is-disabled') || button.getAttribute('aria-disabled') === 'true') {
      return;
    }

    clearTimeout(inputTimer);
    var input = stepper.querySelector('.cart-summary__qty-input');
    var current = parseQty(input ? input.value : stepper.getAttribute('data-qty'));
    var next = button.hasAttribute('data-cart-qty-plus') ? current + 1 : current - 1;
    changeLine(stepper, next);
  });

  document.addEventListener('focusin', function (evt) {
    var input = evt.target;
    if (!input || !input.classList || !input.classList.contains('cart-summary__qty-input')) return;
    input.select();
    var cart = closestCart(input);
    if (cart) cart.classList.add('header-cart--open');
  });

  document.addEventListener('keydown', function (evt) {
    var input = evt.target;
    if (!input || !input.classList || !input.classList.contains('cart-summary__qty-input')) return;
    if (evt.key === 'Enter') {
      evt.preventDefault();
      input.blur();
    }
  });

  document.addEventListener('change', function (evt) {
    var input = evt.target;
    if (!input || !input.classList || !input.classList.contains('cart-summary__qty-input')) return;
    var stepper = input.closest('[data-cart-qty]');
    if (!stepper) return;
    changeLine(stepper, parseQty(input.value));
  });

  document.addEventListener('keyup', function (evt) {
    var input = evt.target;
    if (!input || !input.classList || !input.classList.contains('cart-summary__qty-input')) return;
    var stepper = input.closest('[data-cart-qty]');
    if (!stepper) return;
    if (input.value.length === 0 || parseQty(input.value) < 1) return;
    clearTimeout(inputTimer);
    inputTimer = setTimeout(function () {
      changeLine(stepper, parseQty(input.value));
    }, CHANGE_DELAY);
  });

  document.addEventListener('mouseout', function (evt) {
    if (updating) return;
    var cart = closestCart(evt.target);
    if (!cart) return;
    var related = evt.relatedTarget;
    if (related && cart.contains(related)) return;
    cart.classList.remove('header-cart--open');
  });
})();
